import { useEffect, useState } from 'react'
import { useNavigate, useSearch } from '@tanstack/react-router'
import { AlertCircle } from 'lucide-react'
import { getPkceParams, clearPkceParams, exchangeCode, computeRedirectUri } from '@/lib/pkce'
import { useAuth, isSilentAttempt, clearSilentAttempt } from '@/lib/auth'
import { Spinner } from '@/components/ui/spinner'
import { Button } from '@/components/ui/button'

export default function CallbackPage() {
  const navigate = useNavigate()
  const search = useSearch({ strict: false }) as {
    code?: string
    state?: string
    error?: string
    error_description?: string
  }
  const { setToken, idpConfig, idpConfigLoading } = useAuth()
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    // Wait until IDP config is resolved (may need backend /auth/info for token_endpoint)
    if (idpConfigLoading) return

    const handleCallback = async () => {
      // IDP returned an error (e.g. silent SSO with no active session)
      if (search.error) {
        if (search.error === 'login_required' && isSilentAttempt()) {
          clearSilentAttempt()
          clearPkceParams()
          navigate({ to: '/login' })
          return
        }
        setError(search.error_description || search.error)
        return
      }

      const code = search.code
      if (!code) {
        setError('No authorization code received from IDP.')
        return
      }

      const { verifier, state } = getPkceParams()
      if (!verifier || !state) {
        setError('PKCE session expired or invalid. Please try logging in again.')
        return
      }

      if (search.state !== state) {
        setError('State mismatch — possible CSRF attack. Please try logging in again.')
        return
      }

      if (!idpConfig.token_endpoint || !idpConfig.client_id) {
        setError('IDP token endpoint is not configured. Check backend IDP_ISSUER or set VITE_IDP_TOKEN_URL.')
        return
      }

      try {
        const tokenResponse = await exchangeCode({
          tokenEndpoint: idpConfig.token_endpoint,
          code,
          codeVerifier: verifier,
          clientId: idpConfig.client_id,
          redirectUri: computeRedirectUri(),
        })

        clearPkceParams()
        clearSilentAttempt()
        sessionStorage.removeItem('aip_dns_silent_failed') // Clear silent failure flag
        setToken(tokenResponse.access_token)
        navigate({ to: '/dashboard' })
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Token exchange failed.')
      }
    }

    handleCallback()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idpConfigLoading])

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[var(--bg)] p-4">
        <div className="w-full max-w-md rounded-xl border border-[var(--border)] bg-[var(--bg-card)] p-8">
          <div className="flex flex-col items-center gap-4">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[var(--destructive)]/20">
              <AlertCircle className="h-6 w-6 text-[var(--destructive)]" />
            </div>
            <h2 className="text-lg font-semibold text-[var(--fg)]">Authentication Failed</h2>
            <p className="text-center text-sm text-[var(--fg-muted)]">{error}</p>
            <Button variant="outline" onClick={() => navigate({ to: '/login' })}>
              Back to Login
            </Button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-[var(--bg)]">
      <div className="flex flex-col items-center gap-4">
        <Spinner size="lg" />
        <p className="text-sm text-[var(--fg-muted)]">Completing sign in…</p>
      </div>
    </div>
  )
}
